import { AnyAction, Dispatch, Middleware } from "redux";

import pageViewEnded from "../../events/page_view_ended";
import { ReduxStore } from "../reducers/root_reducer";

let currentPage: string | null = null;
let pageEnteredAt: number | null = null;
let accumulatedMs = 0;
let storeDispatch: Dispatch | null = null;
let listenersAttached = false;

// Exposed for tests to reset module-level state between cases.
export function resetPageViewTrackingState() {
  currentPage = null;
  pageEnteredAt = null;
  accumulatedMs = 0;
}

function now(): number {
  return Date.now();
}

function isTabHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function foregroundElapsed(): number {
  return pageEnteredAt != null ? Math.max(0, now() - pageEnteredAt) : 0;
}

// Tab hidden: bank the foreground time so far and stop the clock. Exported so it can be unit-tested
// without dispatching a real DOM visibilitychange event.
export function pausePageTimer() {
  accumulatedMs += foregroundElapsed();
  pageEnteredAt = null;
}

// Tab visible again: resume the clock (time spent hidden is excluded).
export function resumePageTimer() {
  if (currentPage != null) {
    pageEnteredAt = now();
  }
}

// Flush the current page's dwell as a single event and clear state — the session-end path (pagehide).
// Dispatch happens before the reset; getState still reports the same page, so the re-entrant dispatch
// is a no-op. Exported for tests.
export function flushCurrentPageView() {
  if (storeDispatch && currentPage != null) {
    const dwellMs = accumulatedMs + foregroundElapsed();
    if (dwellMs >= 0) {
      storeDispatch(pageViewEnded(currentPage, dwellMs));
    }
    currentPage = null;
    pageEnteredAt = null;
    accumulatedMs = 0;
  }
}

function attachListeners() {
  if (listenersAttached) {
    return;
  }
  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        pausePageTimer();
      } else if (document.visibilityState === "visible") {
        resumePageTimer();
      }
    });
  }
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("pagehide", () => {
      try {
        flushCurrentPageView();
      } catch (err) {
        console.error("page view flush failed", err);
      }
    });
  }
  listenersAttached = true;
}

const pageViewTrackingMiddleware: Middleware<object, ReduxStore> = store => {
  storeDispatch = store.dispatch;
  attachListeners();

  return (next: Dispatch) => (event: AnyAction) => {
    const result = next(event);

    try {
      const state = store.getState();
      const page: string | null = state?.locationMetadata?.pageName ?? null;

      if (page !== currentPage) {
        const leavingPage = currentPage;
        const dwellMs = accumulatedMs + foregroundElapsed();

        // Update state BEFORE dispatching so the re-entrant dispatch (below) is a no-op.
        currentPage = page;
        pageEnteredAt = page == null || isTabHidden() ? null : now();
        accumulatedMs = 0;

        if (leavingPage != null && dwellMs >= 0) {
          store.dispatch(pageViewEnded(leavingPage, dwellMs));
        }
      }
    } catch (err) {
      console.error("page view tracking failed", err);
    }

    return result;
  };
};

export default pageViewTrackingMiddleware;
